<template>
  <div>
    <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
      <div
        :class="heroClasses"
        v-bind="accessibilityAttrs"
        @click.capture="handleTileLink"
        @keyup.enter="handleTileLink"
      >
        <div class="media-wrapper">
          <media-display
            :media-container="isMediaContainer"
            :sc-image-field="desktopImage"
            :video-source="videoUrl"
            :video-autoplay="autoplay"
            :video-loop="loop"
            :video-mute="mute || autoplay"
            :video-default-controls="!autoplay"
            :video-hidden-text="hiddenVideoTitle"
            @media-error="isVideoFailed = true"
          >
            <picture>
              <source
                media="(max-width: 767px)"
                :srcset="mobileImage.src || desktopImage.cloudinary_3x4_fill_crop"
              >
              <img class="img" :src="desktopImage.src" :alt="desktopImage.alt"/>
            </picture>
          </media-display>
        </div>
        <HeroContent :fields="fields" ref="heroContent"/>
      </div>
    </sc-render>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Image } from '@sitecore-jss/sitecore-jss-vue'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import VimeoVideo from '~/components/sitecore/vimeo-video'
import MediaDisplay from '~/components/sitecore/media-display'
import ScRender from '~/components/sitecore/sc-render'
import HeroContent from './HeroContent'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

const defaultPosition = 'below'

const RATIO = {
  '16_9': '16:9',
  '5_3': '5:3',
  '9_16': '9:16',
  '3_4': '3:4'
}

export default {
  name: 'Hero',

  components: {
    HeroContent,
    ScImage: Image,
    ScRender,
    VimeoVideo,
    MediaDisplay
  },

  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },

  data () {
    return {
      isVideoFailed: false
    }
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapEnvGetters(['isDarkTheme']),
    ...mapFields({
      textPosition: 'textPosition.value'
    }),
    isMobile () {
      return this.$device.isMobile
    },

    desktopVideoRatio () {
      return get(this.fields, 'mainVideoAspectRatio.fields.aspectRatio.value', RATIO['16_9'])
    },

    isPortraitView () {
      return this.desktopVideoRatio === RATIO['9_16']
    },

    isLandscapeView () {
      if (this.isMobile && this.mobileImage && !this.mobileVideoUrl) return false
      if (this.isMobile && this.mobileVideoUrl) return false
      return this.desktopVideoRatio === RATIO['16_9']
    },

    heroClasses () {
      return [
        'hero-content',
        { clickable: this.isClickable },
        { 'dark-theme': this.isDarkTheme },
        { 'portrait-view': this.isPortraitView },
        { 'landscape-view': this.isLandscapeView },
        { 'text-bottom': this.getTextPosition !== defaultPosition }
      ]
    },

    isMediaContainer () {
      if (this.isLandscapeView) return false
      // media display will act as a container for placed images
      return this.isMobile || this.getTextPosition === defaultPosition
    },

    videoUrl () {
      return this.showMobileVideo ? this.mobileVideoUrl : this.desktopVideoUrl
    },

    isVideo () {
      return !this.isVideoFailed && this.videoUrl
    },

    showMobileVideo () {
      return this.isMobile && this.autoplay && this.mobileVideoUrl
    },

    mobileImage () {
      return get(this.fields, 'mobileImage.value', {})
    },

    desktopVideoUrl () {
      return get(this.fields, 'mainVideo.value', '')
    },

    mobileVideoUrl () {
      return get(this.fields, 'mobileVideo.value', '')
    },

    autoplay () {
      return get(this.fields, 'autoplay.value', false)
    },

    loop () {
      return get(this.fields, 'loop.value', false)
    },

    mute () {
      return get(this.fields, 'mute.value', false)
    },

    hiddenVideoTitle () {
      return get(this.fields, 'hiddenVideoTitle.value', '')
    },

    desktopImage () {
      return get(this.fields, 'mainImage.value', {})
    },

    cta1 () {
      return get(this.fields, 'cta1.value', {})
    },

    cta2 () {
      return get(this.fields, 'cta2.value', {})
    },

    getTextPosition () {
      return (this.textPosition && this.textPosition.split(' ')[1]) || defaultPosition
    },

    isClickable () {
      return (this.cta2.href || this.cta1.href)
        && !(this.cta2.href && this.cta1.href)
        && !this.videoUrl
    },

    accessibilityAttrs () {
      if (this.videoUrl) {
        return { tabindex: '0' }
      }

      return this.isClickable ? { role: 'link', tabindex: '0' } : {}
    }
  },

  methods: {
    handleTileLink () {
      if (!this.isClickable) return
      this.$refs.heroContent.contentAction()
    }
  }
}
</script>

<style lang="scss" scoped>

@mixin black-border {
  border-top: $space-060 solid $color-black;
  border-bottom: $space-060 solid $color-black;
}

.hero-content {
  position: relative;
  margin-bottom: $space-090;

  @include tablet() {
    margin-bottom: $space-100;
  }

  &.clickable {
    cursor: pointer;
  }

  &.landscape-view {
    .media-wrapper {
      position: relative;
      @include ratio(16, 9);
    }
  }

  &.portrait-view {
    .media-wrapper {
      @include tablet {
        text-align: center;
        background-color: $color-black;
        @include black-border;

        img {
          // set the image width relative to the hero desktop ratio for applying equivalent images sizes
          width: #{(2 - (5 / 3)) * 100%};
        }
      }
    }

    &.text-bottom {
      @include tablet {
        @include black-border;
      }

      .media-wrapper {
        @include tablet {
          border: none;
          @include ratio(5, 3);
        }
      }
    }
  }
}

</style>
